import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProductById } from "../../services/OnlineStore";
import styles from "./productVariable.module.css";
import { addToCart } from "../../services/OnlineStore";
import useCartStore from "../../states/CartStore";
import Page from "../../components/page/Page.jsx";
import { IoChevronBack } from "react-icons/io5";
import { LuImage } from "react-icons/lu";
function getVariantImages(variant) {
    return Array.isArray(variant?.images) ? variant.images.filter(Boolean) : [];
}

function ProductDetails() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { setCart } = useCartStore.getState();

    const [product, setProduct] = useState(null);
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [quantity, setQuantity] = useState(1);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [failedImages, setFailedImages] = useState(() => new Set());
    const [loadedImages, setLoadedImages] = useState(() => new Set());
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 600);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 600);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const markImageFailed = (url) => {
        if (!url) return;
        setFailedImages((prev) => {
            if (prev.has(url)) return prev;
            const next = new Set(prev);
            next.add(url);
            return next;
        });
    };

    const markImageLoaded = (url) => {
        if (!url) return;
        setLoadedImages((prev) => {
            if (prev.has(url)) return prev;
            const next = new Set(prev);
            next.add(url);
            return next;
        });
    };

    useEffect(() => {
        getProductById(productId)
            .then((product) => {
                setProduct(product);

                const variants = Array.isArray(product?.variants) ? product.variants : [];

                if (variants.length > 0) {
                    const defaults = {};

                    (variants[0].attributes || []).forEach((attr) => {
                        defaults[attr.name] = attr.value;
                    });

                    setSelectedAttributes(defaults);
                }
            })
            .catch(console.error);
    }, [productId]);

    const attributeGroups = useMemo(() => {
        if (!product) return {};

        const groups = {};

        const variants = Array.isArray(product.variants) ? product.variants : [];

        variants.forEach((variant) => {
            (variant.attributes || []).forEach((attr) => {
                if (!groups[attr.name]) {
                    groups[attr.name] = [];
                }

                if (!groups[attr.name].includes(attr.value)) {
                    groups[attr.name].push(attr.value);
                }
            });
        });

        return groups;
    }, [product]);

    const selectedVariant = useMemo(() => {
        if (!product) return null;

        const variants = Array.isArray(product.variants) ? product.variants : [];

        if (variants.length === 0) {
            return null;
        }

        return (
            variants.find((variant) =>
                (variant.attributes || []).every(
                    (attr) => selectedAttributes[attr.name] === attr.value
                )
            ) || variants[0]
        );
    }, [product, selectedAttributes]);

    useEffect(() => {
        setSelectedImageIndex(0);
    }, [selectedVariant?.id]);

        const variantImages = useMemo(() => getVariantImages(selectedVariant), [selectedVariant]);
    const activeImage = variantImages[selectedImageIndex] || variantImages[0] || null;

    useEffect(() => {
        if (variantImages.length < 2) return;
		let active = true;

        const adjacentIndexes = [
            (selectedImageIndex + 1) % variantImages.length,
            (selectedImageIndex - 1 + variantImages.length) % variantImages.length,
        ];

        adjacentIndexes.forEach((index) => {
            const url = variantImages[index]?.url;
            if (!url) return;

            const image = new Image();
            image.src = url;
            image.onload = () => {
				if (!active) return;
				setLoadedImages((prev) => new Set(prev).add(url));
			};
            image.onerror = () => {
				if (!active) return;
				setFailedImages((prev) => new Set(prev).add(url));
			};
        });

		return () => {
			active = false;
		};
    }, [selectedImageIndex, variantImages]);

    if (!product) {
        return (
            <Page skinny={true}>
                <div className={styles.productLoading} aria-label="Loading product" />
            </Page>
        );
    }

    if (!selectedVariant) {
        return (
            <div className={styles.page}>
                <button
                    className={styles.backButton}
                    onClick={() => navigate(-1)}
                >
                    ← Back
                </button>

                <div className={styles.info}>
                    <h1>{product.name}</h1>
                    <p>This product is currently unavailable.</p>
                </div>
            </div>
        );
    }

    const activeImageFailed = !activeImage?.url || failedImages.has(activeImage.url);
    const activeImageLoaded = activeImage?.url && loadedImages.has(activeImage.url);
    const maxQuantity = Math.max(1, Math.min(10, selectedVariant.stock || 0));

    const handleAddToCart = async () => {
        const cartToken = localStorage.getItem("cartToken");

        const payload = {
            cart_token: cartToken,
            variant_id: selectedVariant.id,
            quantity,
        };

        console.log(payload);

        const response = await addToCart(payload);
        setCart(response.cart);
        localStorage.setItem("cartToken", response.cart.cart_token);
    };

    function normalizeStockMessage(stock) {
        if (stock <= 0) {
            return "Out of Stock";
        } else if (stock < 10) {
            return `Only ${stock} left!`;
        } else {
            return "In Stock";
        }
    }

    function findStockColor(stock) {
        if (stock <= 0) {
            return "var(--danger)";
        } else if (stock < 10) {
            return "var(--warning)";
        } else {
            return "var(--success)";
        }
    }


    return (
        <Page
            skinny={true}
        >
                <div className={styles.backRow}>
                    <button
                        className="btn btnSmall"
                        style={{ backgroundColor: "var(--page)", color: "var(--text-muted)", border: "1px solid var(--border-subtle)" }}
                        onClick={() => navigate(-1)}
                    >
                        <IoChevronBack /> Back
                    </button>
                </div>

                <div
                    className={`${styles.productContainer} ${variantImages.length === 0 ? styles.productContainerNoImages : ""}`}
                    style={isMobile ? undefined : { gridTemplateColumns: variantImages.length > 0 ? "3fr 2fr" : "1fr 1fr" }}
                >
                    <div
                        className={`${styles.gallery} ${variantImages.length <= 1 ? styles.galleryNoThumbnails : ""}`}
                        style={isMobile ? undefined : { gridTemplateColumns: variantImages.length > 1 ? "1fr 5fr" : "1fr" }}
                    >

                        {variantImages.length > 1 && <div className={styles.thumbnailRow}>
                            <div className={styles.thumbnailRowScroll}>
                                {variantImages.map((image, index) => {
                                    const failed = !image.url || failedImages.has(image.url);

                                    return (
                                        <div
                                            className={
                                                index === selectedImageIndex
                                                    ? `${styles.thumbnailWrapper} ${styles.activeThumbnail}`
                                                    : styles.thumbnailWrapper
                                            }
                                            key={index}
                                            onClick={() => setSelectedImageIndex(index)}
                                        >
                                            {failed ? (
                                                <LuImage className={styles.imagePlaceholder} />
                                            ) : (
                                                <img
                                                    className={styles.thumbnail}
                                                    src={image.url}
                                                    alt={image.alt_text}
                                                    onError={() => markImageFailed(image.url)}
                                                />
                                            )}
                                        </div>)
                                })}
                            </div>
                                
                        </div>}

                        <div className={styles.mainImageContainer}>
                            {activeImageFailed ? (
                                <LuImage className={styles.imagePlaceholder} />
                            ) : (
                                <>
                                    {!activeImageLoaded && <div className={styles.imageLoading} aria-hidden="true" />}
                                    <img
                                        className={`${styles.mainImage} ${activeImageLoaded ? styles.mainImageLoaded : ""}`}
                                        src={activeImage.url}
                                        alt={activeImage.alt_text || product.name}
                                        onLoad={() => markImageLoaded(activeImage.url)}
                                        onError={() => markImageFailed(activeImage.url)}
                                    />
                                </>
                            )}
                        </div>


                    </div>
                    

                        <div className={styles.info}>
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                gap: "var(--space-2)",
                                width: "100%",
                                height: "100%",
                            }}>
                                <div className={styles.priceContainer}>
                                    <p className={styles.price}>
                                        ${(selectedVariant.effective_price_cents / 100).toFixed(2)}

                                    </p>
                                    <span className={styles.stock} style={{ color: findStockColor(selectedVariant.stock) }}>
                                        {normalizeStockMessage(selectedVariant.stock)}
                                    </span>
                                </div>

                                <div className={styles.productName}>
                                    {product.name}
                                </div>

                                <p className={styles.productDescription}>{product.description}</p>
                            </div>

                            <div className={styles.attributesContainer}>
                                {Object.entries(attributeGroups).map(([attributeName, values]) => (
                                    <div key={attributeName} className={styles.attributeGroup}>

                                        <p className={styles.attributeName}>{attributeName}</p>

                                        <div className={styles.attributeButtons}>
                                            {values.map((value) => (
                                                <button
                                                    key={value}
                                                    className={
                                                        selectedAttributes[attributeName] === value
                                                            ? styles.activeAttribute
                                                            : styles.attributeButton
                                                    }
                                                    onClick={() =>
                                                        setSelectedAttributes(prev => ({
                                                            ...prev,
                                                            [attributeName]: value
                                                        }))
                                                    }
                                                >
                                                    {value}
                                                </button>
                                            ))}
                                        </div>

                                    </div>
                                ))}

                                <div className={styles.quantity}>
                                    <p>Quantity</p>

                                    <select
                                        value={quantity}
                                        onChange={(e) => setQuantity(Number(e.target.value))}
                                    >
                                        {[...Array(maxQuantity).keys()].map(i => (
                                            <option key={i + 1} value={i + 1}>
                                                {i + 1}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                            </div>




                            <button
                                className={styles.addToCart}
                                onClick={handleAddToCart}
                            >
                                Add To Cart
                            </button>

                        </div>


                </div>

        </Page>
    );
}

export default ProductDetails;